import React, { useState, useRef, useEffect } from "react";
import moment from "moment-timezone";
import { AnimatePresence, motion } from "framer-motion";
import {
  FaUtensils,
  FaSkullCrossbones,
  FaWeightHanging,
  FaTractor,
  FaUserTie,
  FaLayerGroup,
  FaChevronRight,
  FaExpand,
  FaCompress,
  FaTimes,
  FaArrowLeft,
  FaRedo,
  FaPaperPlane,
  FaCalendarAlt,
  FaCheck,
  FaSearch,
  FaClipboardList,
  FaChartLine,
  FaImage,
} from "react-icons/fa";

import MessageBubble from "./MessageBubble";
import ReportCard from "./ReportCard";
import DayGrid from "./DayGrid";
import BatchSummary from "./BatchSummary";
import CategoryCards from "./CategoryCards";
import { normalizeReportResponse } from "./reportUtils";
import { THEME } from "./theme";
import { useTranslation } from "react-i18next";
import * as chatApi from "./chatApi";
import { isLoggedin, getUser, getRole } from "../utils/common";
import { Role } from "../utils/role";

// Roo the Rooster mascot artwork (served from /public/dist/img).
const MASCOT_IMG = "/dist/img/ipoultry_masket.png";

// ---- Conversation steps (finite state machine) --------------------------
//
// The step ORDER mirrors the mobile app's Roo flow (ipoultry-mobile
// src/Report/Report.js): a welcome menu offers Enter Data or Reports &
// Analysis; both branches then share farmer/farm/batch selection. Data entry
// continues batch overview -> day grid -> per-day category cards -> value
// entry, looping back to the day grid after every save. Reports continue
// report-type (plus a date range for custom_range) -> generated report card.
// The free-text bar at the bottom is available throughout, like mobile's
// "Ask Roo anything…".
const STEP = {
  LANGUAGE: "SELECT_LANGUAGE",
  WELCOME: "WELCOME_MENU",
  FARMER: "SELECT_FARMER", // admin-only: pick a farmer before their farms
  FARM: "SELECT_FARM",
  BATCH: "SELECT_BATCH",
  SUMMARY: "BATCH_SUMMARY", // batch overview card, read before the day grid
  DAY: "SELECT_DAY",
  DAY_ACTIONS: "DAY_ACTIONS", // "What data would you like to enter?"
  ENTER: "ENTER_VALUE",
  REPORT_MENU: "REPORT_MENU", // today | yesterday | weekly | custom | phase
  DATE_RANGE: "DATE_RANGE", // custom_range only
  REPORT_VIEW: "REPORT_VIEW", // a generated report is on screen
};

const REPORT_TYPES = ["today", "yesterday", "weekly", "custom_range", "phase_wise"];

const present = (v) => v !== undefined && v !== null && String(v).trim() !== "";
const isPositive = (v) => Number(v) > 0; // null/undefined/""/0 -> false

// Has this category been recorded for the day?
//   Feed / Mortality(+Culling) -> *_status flags (a genuinely entered 0 counts).
//   Weight                     -> value > 0 (a 0/null weight is NOT a real entry).
// Covers sexed & non-sexed via the OR across the per-sex fields.
const isEntered = (ex, action) => {
  if (!ex) return false;
  if (action === "FEED") return !!(ex.feed_status || ex.feed_male_status || ex.feed_female_status);
  if (action === "WEIGHT")
    return [ex.checkWeight, ex.chickWeight, ex.chickWeight_male, ex.chickWeight_female].some(
      isPositive
    );
  // MORTALITY (includes culling)
  return !!(
    ex.mortality_status ||
    ex.mortality_male_status ||
    ex.mortality_female_status ||
    ex.culling_status ||
    ex.culling_male_status ||
    ex.culling_female_status
  );
};

// The existing-record VALUE keys per category (for the generic value display).
const valueFieldsFor = (action, sexing) => {
  if (action === "FEED") return ["feed", "feed_male", "feed_female"];
  if (action === "WEIGHT")
    return sexing ? ["chickWeight_male", "chickWeight_female"] : ["checkWeight", "chickWeight"];
  return sexing
    ? ["mortality_male", "mortality_female", "culling_male", "culling_female"]
    : ["mortality", "culling"];
};

// Sexing-aware current-value label (e.g. "M 1.2 · F 1.1").
// Weight values are only shown when > 0 (0 = not a real entry); mortality/feed
// show any present value (a real 0 is meaningful).
const existingSummary = (ex, action, sexing) => {
  if (!ex) return "";
  if (action === "WEIGHT") {
    const parts = [];
    if (sexing) {
      if (isPositive(ex.chickWeight_male)) parts.push(`M ${ex.chickWeight_male}`);
      if (isPositive(ex.chickWeight_female)) parts.push(`F ${ex.chickWeight_female}`);
    } else {
      const w = [ex.checkWeight, ex.chickWeight].find(isPositive);
      if (w !== undefined) parts.push(`${w}`);
    }
    return parts.join(" · ");
  }
  if (sexing && action === "MORTALITY") {
    const parts = [];
    if (present(ex.mortality_male)) parts.push(`M ${ex.mortality_male}`);
    if (present(ex.mortality_female)) parts.push(`F ${ex.mortality_female}`);
    if (present(ex.culling_male)) parts.push(`CM ${ex.culling_male}`);
    if (present(ex.culling_female)) parts.push(`CF ${ex.culling_female}`);
    if (parts.length) return parts.join(" · ");
  }
  const v = valueFieldsFor(action, sexing)
    .map((k) => ex[k])
    .find(present);
  return present(v) ? v : "";
};

// ---- Batch summary metadata (from getGrowthCurve `others`) --------------
// getGrowthCurve gives reliable batch metadata. Its per-day VALUES are NOT a
// reliable "entered" signal (curve/ideal figures exist for every day), so the
// completeness colours come from the dedicated status API below, not from here.
export const buildGrowth = (res) => {
  const ok = !!(res && res.status !== false && res.others);
  return { ok, others: res?.others || null };
};

// ---- Per-day completeness from the bulk status API ----------------------
// Expects getDayStatuses -> { list: [ { date, feed_status, chickWeight_status,
// mortality_status, culling_status, ...sexed variants } ] }, where each item
// uses the SAME field names as the single-day getDailyOperationsStatus, so the
// existing isEntered() helper classifies each day directly.
// Returns { statusOk, dayStatusMap, done }.
export const buildStatusMap = (statusRes) => {
  const list = Array.isArray(statusRes?.list) ? statusRes.list : null;
  const map = new Map();
  let done = 0;
  if (list) {
    list.forEach((it) => {
      if (!it || !it.date) return;
      const key = moment(it.date).format("YYYY-MM-DD");
      const feed = isEntered(it, "FEED");
      const weight = isEntered(it, "WEIGHT");
      const mortality = isEntered(it, "MORTALITY");
      const count = [feed, weight, mortality].filter(Boolean).length;
      const complete = count === 3 ? "full" : count > 0 ? "partial" : "none";
      if (complete === "full") done += 1;
      map.set(key, { feed, weight, mortality, complete });
    });
  }
  return { statusOk: !!list, dayStatusMap: map, done };
};

// ---- Validation rules (mirrors DailyOperations.js) ----------------------
const REGEX = {
  feed: /^\d+(\.\d{1,3})?$/, // KG, up to 3 decimals
  weight: /^[0-5](\.\d+)?$/, // KG, 0-5 range
  int: /^[0-9]+$/, // whole number of birds
};

// Maps a save `type` to the key returned by getExistingValue (for pre-fill).
const EXISTING_KEY = {
  feed: "feed",
  chick: "checkWeight",
  chick_male: "chickWeight_male",
  chick_female: "chickWeight_female",
  mortality: "mortality",
  mortality_male: "mortality_male",
  mortality_female: "mortality_female",
  culling: "culling",
  culling_male: "culling_male",
  culling_female: "culling_female",
};

// The user's first name from the stored session (best-effort).
const firstName = () => {
  let u = {};
  try {
    u = getUser() || {};
  } catch (e) {
    u = {};
  }
  const full = (u.farmerName || u.name || u.firstName || u.userName || "").trim();
  return full ? full.split(/\s+/)[0] : "";
};

// Time-of-day greeting (Asia/Singapore), localized.
const greetingWord = (t) => {
  const h = moment().tz("Asia/Singapore").hour();
  if (h < 12) return t.goodMorning;
  if (h < 17) return t.goodAfternoon;
  return t.goodEvening;
};

// Animated welcome panel shown at the language step.
const WelcomeHero = ({ t, name }) => {
  const greeting = greetingWord(t);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "20px 12px 8px",
      }}
    >
      <motion.div
        animate={{ y: [0, -7, 0] }}
        transition={{ repeat: Infinity, duration: 2.6, ease: "easeInOut" }}
        style={{
          position: "relative",
          width: 78,
          height: 78,
          borderRadius: "50%",
          background: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 10px 26px rgba(37,99,235,0.42)",
          marginBottom: 16,
        }}
      >
        <motion.span
          aria-hidden
          animate={{ scale: [1, 1.35, 1], opacity: [0.5, 0, 0.5] }}
          transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
          style={{
            position: "absolute",
            inset: -6,
            borderRadius: "50%",
            border: "2px solid #93c5fd",
          }}
        />
        <img
          src={MASCOT_IMG}
          alt={t.mascotName}
          style={{ width: 58, height: 58, objectFit: "contain", display: "block" }}
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        style={{
          marginBottom: 10,
          padding: "3px 12px",
          borderRadius: 999,
          background: THEME.soft,
          color: THEME.blue,
          fontSize: 12.5,
          fontWeight: 800,
          letterSpacing: 0.3,
        }}
      >
        {t.mascotName}
      </motion.div>

      <motion.h3
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        style={{ margin: 0, fontSize: 19, fontWeight: 800, color: "#0f172a" }}
      >
        {greeting}
        {name ? `, ${name}` : ""}{" "}
        <motion.span
          style={{ display: "inline-block", transformOrigin: "70% 70%" }}
          animate={{ rotate: [0, 18, -8, 18, 0] }}
          transition={{ repeat: Infinity, repeatDelay: 1.6, duration: 0.9 }}
        >
          👋
        </motion.span>
      </motion.h3>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25 }}
        style={{ margin: "9px 0 0", fontSize: 13, color: "#64748b", lineHeight: 1.5, maxWidth: 290 }}
      >
        {t.assistantIntro}
      </motion.p>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35 }}
        style={{
          marginTop: 16,
          fontSize: 11.5,
          fontWeight: 700,
          color: "#2563eb",
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {t.chooseLanguage}
      </motion.div>
    </motion.div>
  );
};

// The three per-day data categories with their icons.
const ACTIONS = [
  { value: "FEED", Icon: FaUtensils },
  { value: "MORTALITY", Icon: FaSkullCrossbones },
  { value: "WEIGHT", Icon: FaWeightHanging },
];

// Builds the list of input fields for a category, honouring bird-sexing.
const fieldsFor = (action, sexing, t) => {
  if (action === "FEED") {
    return [{ type: "feed", label: t.enterFeed, rule: "feed" }];
  }
  if (action === "WEIGHT") {
    if (sexing) {
      return [
        { type: "chick_male", label: `${t.enterWeight} — ${t.male}`, rule: "weight" },
        { type: "chick_female", label: `${t.enterWeight} — ${t.female}`, rule: "weight" },
      ];
    }
    return [{ type: "chick", label: t.enterWeight, rule: "weight" }];
  }
  // MORTALITY
  if (sexing) {
    return [
      { type: "mortality_male", label: `${t.enterMortality} — ${t.male}`, rule: "int" },
      { type: "mortality_female", label: `${t.enterMortality} — ${t.female}`, rule: "int" },
      { type: "culling_male", label: `${t.enterCulling} — ${t.male}`, rule: "int" },
      { type: "culling_female", label: `${t.enterCulling} — ${t.female}`, rule: "int" },
    ];
  }
  return [
    { type: "mortality", label: t.enterMortality, rule: "int" },
    { type: "culling", label: t.enterCulling, rule: "int" },
  ];
};

const blankData = {
  mode: "", // "data_entry" | "report"
  reportType: "", // today | yesterday | weekly | custom_range | phase_wise
  startDate: "", // custom_range only
  endDate: "",
  action: "", // FEED | MORTALITY | WEIGHT (data entry)
  farmerID: "", // admin flow only
  farmerName: "",
  farmID: "",
  farmName: "",
  batchID: "",
  batchName: "",
  date: "",
  day: "",
  sexing: false,
  existing: {},
};

// Staggered list animation for selection cards.
const listContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.05 } } };
const listItem = {
  hidden: { opacity: 0, x: -12 },
  visible: { opacity: 1, x: 0 },
};

// A selectable card list (used for farm & batch). Shows a search bar once
// there are more than 5 items. The search text is LOCAL state so typing only
// re-renders this component (the input never loses focus), and it resets
// whenever a new list is loaded.
export const SelectList = ({ items, Icon, t, onSelect }) => {
  const [query, setQuery] = useState("");
  useEffect(() => {
    setQuery("");
  }, [items]);

  const showSearch = items.length > 5;
  const q = query.trim().toLowerCase();
  const filtered =
    showSearch && q
      ? items.filter((o) => String(o.label ?? "").toLowerCase().includes(q))
      : items;
  return (
    <div>
      {showSearch && (
        <div style={{ position: "relative", marginBottom: 10 }}>
          <FaSearch
            size={12}
            color={THEME.grey}
            style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
          />
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.searchPlaceholder}
            onFocus={(e) => (e.target.style.borderColor = THEME.blue)}
            onBlur={(e) => (e.target.style.borderColor = "#dbe3ee")}
            style={{
              width: "100%",
              padding: "9px 12px 9px 32px",
              border: "1px solid #dbe3ee",
              borderRadius: 10,
              fontSize: 13.5,
              boxSizing: "border-box",
              outline: "none",
            }}
          />
        </div>
      )}
      {filtered.length === 0 ? (
        <div style={{ fontSize: 13, color: THEME.grey, padding: "6px 2px" }}>{t.noMatches}</div>
      ) : (
        <div>
          {filtered.map((opt, idx) => (
            <motion.button
              key={`${opt.value}-${idx}`}
              type="button"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.18, delay: Math.min(idx * 0.03, 0.25) }}
              whileHover={{ scale: 1.015, borderColor: THEME.blue }}
              whileTap={{ scale: 0.985 }}
              onClick={() => onSelect(opt)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                width: "100%",
                textAlign: "left",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: "11px 13px",
                marginBottom: 8,
                cursor: "pointer",
                boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  background: THEME.soft,
                  color: THEME.blue,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon size={15} />
              </span>
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: THEME.ink }}>
                {opt.label}
              </span>
              <FaChevronRight size={12} color={THEME.grey} />
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
};

const ChickenChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [step, setStep] = useState(STEP.LANGUAGE);
  const [messages, setMessages] = useState([]);
  const [options, setOptions] = useState([]);
  const [data, setData] = useState(blankData);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // { text, retry }
  const [inputs, setInputs] = useState({});
  const [inputErrors, setInputErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null); // { type:'error', text }
  const [confirm, setConfirm] = useState(null); // { payload, lines } pending confirmation
  const [growth, setGrowth] = useState(null); // { ok, others, dayStatusMap, progress } | null
  const [currentReport, setCurrentReport] = useState(null); // context for the chat bar

  // Set once the user asks a free-text question. The welcome menu's two
  // branch buttons are a "pick where to start" prompt, so they stop making
  // sense the moment the conversation has moved on -- they hide, and the
  // header's restart button is how you get back to them.
  const [askedFreeText, setAskedFreeText] = useState(false);

  // Free-text "Ask Roo anything…" bar (Django reports/chat/).
  const [chatInput, setChatInput] = useState("");
  const [chatImage, setChatImage] = useState(null); // { file, url }
  const [chatLoading, setChatLoading] = useState(false);
  const chatFileRef = useRef(null);

  // Language comes from the app-wide switcher (see src/i18n). The bundle is
  // read as a plain object because this widget uses `t.someKey` and its own
  // single-brace interpolation rather than i18next's t('key') API.
  const { i18n } = useTranslation("chat");
  // Plain lookup rather than useMemo: the i18n object identity does not change
  // on a language switch, so memoising on [i18n] would serve a stale bundle.
  // getResourceBundle is a cheap store read.
  const t =
    i18n.getResourceBundle(i18n.resolvedLanguage, "chat") ||
    i18n.getResourceBundle("en", "chat");
  const scrollRef = useRef(null);

  // Admins own no farms directly — they pick a farmer first, then that farmer's
  // active-batch farms. Everyone else goes straight to their own farms.
  //
  // Guarded: getRole() reads localStorage 'user' and dereferences it without a
  // null check, so it throws with no session. This runs before the
  // `if (!isLoggedin()) return null` bail-out below — which cannot move up,
  // since the hooks above it must run unconditionally.
  const isAdmin = isLoggedin() && getRole() === Role.Admin;

  // Auto-scroll to the latest message.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, step, loading, chatLoading, error, saveMsg]);

  // ---- conversation helpers --------------------------------------------
  const botSay = (text) => setMessages((m) => [...m, { from: "bot", text }]);
  const userSay = (text) => setMessages((m) => [...m, { from: "user", text }]);

  const resetConversation = () => {
    setStep(STEP.LANGUAGE);
    setMessages([]);
    setAskedFreeText(false);
    setOptions([]);
    setData(blankData);
    setError(null);
    setInputs({});
    setInputErrors({});
    setSaveMsg(null);
    setConfirm(null);
    setGrowth(null);
    setCurrentReport(null);
    setChatInput("");
    setChatImage(null);
    setChatLoading(false);
    setLoading(false);
    setSaving(false);
  };

  const launch = () => {
    resetConversation(); // the animated welcome hero renders from the LANGUAGE step
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setMaximized(false);
    resetConversation();
  };

  // Header restart: same clean slate as reopening the widget, without closing
  // it -- back to the landing screen (the LANGUAGE step's welcome hero).
  const handleRestart = () => {
    resetConversation();
  };

  // ---- loaders (each wrapped with graceful error handling) -------------
  // Admin-only: the farmers whose farms an admin can drill into.
  const loadFarmers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await chatApi.getFarmers();
      const list = (res?.list || []).map((f) => ({ label: f.farmerName, value: f.id }));
      setOptions(list);
      if (!list.length) botSay(t.noFarmers);
    } catch (e) {
      setOptions([]);
      setError({ text: t.loadError, retry: loadFarmers });
    } finally {
      setLoading(false);
    }
  };

  // For admins, scope to the chosen farmer's farms; otherwise the user's own.
  // farmerID is passed explicitly to avoid reading not-yet-committed state.
  const loadFarms = async (farmerID = data.farmerID) => {
    setLoading(true);
    setError(null);
    try {
      const res = isAdmin
        ? await chatApi.getFarmsForFarmer(farmerID)
        : await chatApi.getFarms();
      const list = (res?.list || []).map((f) => ({ label: f.farmName, value: f.farmID }));
      setOptions(list);
      if (!list.length) botSay(t.noFarms);
    } catch (e) {
      setOptions([]);
      setError({ text: t.loadError, retry: () => loadFarms(farmerID) });
    } finally {
      setLoading(false);
    }
  };

  const loadBatches = async (farmID) => {
    setLoading(true);
    setError(null);
    try {
      const res = await chatApi.getActiveBatches(farmID);
      const list = (res?.list || []).map((b) => ({ label: b.batchName, value: b.batchID }));
      setOptions(list);
      if (!list.length) botSay(t.noBatches);
    } catch (e) {
      setOptions([]);
      setError({ text: t.loadError, retry: () => loadBatches(farmID) });
    } finally {
      setLoading(false);
    }
  };

  // silent: skip own loading/error toggles when driven by loadBatchOverview.
  const loadDays = async (batchID, { silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await chatApi.getDays(batchID);
      const today = moment().tz("Asia/Singapore");
      // Same day-grid model as the Batch Operations screen: every batch day,
      // numbered (index - 1), DOC marker on the first, today highlighted.
      const list = (res?.list || [])
        .filter((it) => it.date)
        .map((it) => ({
          value: moment(it.date).format("YYYY-MM-DD"), // matches growth status-map keys
          day: it.index - 1, // day number shown in the cell
          phase: it.phase,
          doc: it.index === 1,
          isToday: moment(it.date).isSame(today, "day"),
          isFuture: moment(it.date).isAfter(today, "day"),
        }))
        .sort((a, b) => (a.value < b.value ? -1 : 1)); // DOC -> latest (ascending)
      setOptions(list);
      if (!list.length && !silent) botSay(t.noDays);
    } catch (e) {
      setOptions([]);
      if (!silent) setError({ text: t.loadError, retry: () => loadDays(batchID) });
      else throw e; // let the caller surface the error
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Loads everything needed for the day grid, in parallel:
  //   - growthcurve  -> batch metadata (breed, birds, duration, sexing…)
  //   - day statuses -> per-day completeness colours (graceful: neutral cells
  //                     if the endpoint isn't available yet)
  //   - day grid     -> the numbered day cells
  const loadBatchOverview = async (batchID) => {
    setLoading(true);
    setError(null);
    setGrowth(null); // don't flash the previous batch's overview
    try {
      const [gcRes, statusRes] = await Promise.all([
        chatApi.getGrowthCurve(batchID).catch(() => null),
        chatApi.getDayStatuses(batchID).catch(() => null), // optional; colours neutral on failure
        loadDays(batchID, { silent: true }),
      ]);
      const meta = buildGrowth(gcRes);
      const status = buildStatusMap(statusRes);
      const total = Number(meta.others?.batchDuration) || status.dayStatusMap.size || 0;
      setGrowth({
        ok: meta.ok,
        others: meta.others,
        statusOk: status.statusOk,
        dayStatusMap: status.dayStatusMap,
        progress: { done: status.done, total },
      });
    } catch (e) {
      setGrowth({ ok: false, others: null, statusOk: false, dayStatusMap: new Map(), progress: { done: 0, total: 0 } });
      setError({ text: t.loadError, retry: () => loadBatchOverview(batchID) });
    } finally {
      setLoading(false);
    }
  };

  // ---- step handlers ----------------------------------------------------
  const chooseLanguage = (code) => {
    // Writes the app-wide preference (persisted to localStorage 'lang'), so the
    // chat and the rest of the UI can never disagree.
    const lng = code === "BM" ? "ms" : "en";
    i18n.changeLanguage(lng);
    const tt = i18n.getResourceBundle(lng, "chat") || t;
    userSay(code === "EN" ? "English" : "Bahasa Melayu");
    botSay(tt.welcomeIntro);
    setAskedFreeText(false);
    setStep(STEP.WELCOME);
  };

  // Shared by both branches: admins pick a farmer first, everyone else goes
  // straight to their farms.
  const gotoFarmOrFarmer = () => {
    if (isAdmin) {
      botSay(t.selectFarmer);
      setStep(STEP.FARMER);
      loadFarmers();
    } else {
      botSay(t.selectFarm);
      setStep(STEP.FARM);
      loadFarms();
    }
  };

  const chooseEnterData = () => {
    userSay(t.menuEnterData);
    setData({ ...blankData, mode: "data_entry" });
    gotoFarmOrFarmer();
  };

  const chooseReports = () => {
    userSay(t.menuReports);
    setData({ ...blankData, mode: "report" });
    botSay(t.whatAnalyze);
    setStep(STEP.REPORT_MENU);
  };

  const backToWelcome = () => {
    botSay(t.welcomeIntro);
    setAskedFreeText(false);
    setStep(STEP.WELCOME);
  };

  const chooseReportType = (opt) => {
    userSay(opt.label);
    setData((d) => ({ ...d, reportType: opt.value, startDate: "", endDate: "" }));
    if (opt.value === "custom_range") {
      botSay(t.chooseDates);
      setStep(STEP.DATE_RANGE);
    } else {
      gotoFarmOrFarmer();
    }
  };

  const applyRange = () => {
    const { startDate, endDate } = data;
    if (!startDate || !endDate || startDate > endDate) {
      setSaveMsg({ type: "error", text: t.invalidRange });
      return;
    }
    setSaveMsg(null);
    userSay(`${startDate} → ${endDate}`);
    gotoFarmOrFarmer();
  };

  const chooseFarmer = (opt) => {
    userSay(opt.label);
    setData((d) => ({ ...d, farmerID: opt.value, farmerName: opt.label }));
    botSay(t.selectFarm);
    setStep(STEP.FARM);
    loadFarms(opt.value);
  };

  const chooseFarm = (opt) => {
    userSay(opt.label);
    setData((d) => ({ ...d, farmID: opt.value, farmName: opt.label }));
    botSay(t.selectBatch);
    setStep(STEP.BATCH);
    loadBatches(opt.value);
  };

  // Both branches converge here: data entry goes on to the day grid, the
  // report branch generates its report right away (as on mobile).
  const chooseBatch = (opt) => {
    userSay(opt.label);
    setData((d) => ({ ...d, batchID: opt.value, batchName: opt.label }));
    if (data.mode === "report") {
      runReport(opt.value, data.farmID);
    } else {
      botSay(t.batchSummaryTitle);
      setStep(STEP.SUMMARY);
      loadBatchOverview(opt.value);
    }
  };

  // The overview card's "Proceed". loadBatchOverview already fetched the day
  // grid alongside the metadata, so this is a pure step change unless that
  // fetch came back empty.
  const proceedToDays = () => {
    userSay(t.proceed);
    botSay(t.selectDay);
    setStep(STEP.DAY);
    if (!options.length) loadDays(data.batchID);
  };

  // ---- report generation (Django service, same as mobile) ---------------
  const runReport = async (batchID, farmID, reportType = data.reportType) => {
    setStep(STEP.REPORT_VIEW);
    setLoading(true);
    setError(null);
    botSay(t.generating);
    try {
      const payload = { report_type: reportType, farm_id: farmID, batch_id: batchID };
      if (reportType === "custom_range") {
        payload.start_date = data.startDate;
        payload.end_date = data.endDate;
      }
      const res =
        reportType === "phase_wise"
          ? await chatApi.generatePhaseWiseReport(payload)
          : await chatApi.generateReport(payload);
      const report = normalizeReportResponse(res, { reportType, farmId: farmID, batchId: batchID });
      if (!report) throw new Error("Empty report response");
      setCurrentReport(report);
      setMessages((m) => [...m, { from: "bot", report }]);
    } catch (e) {
      setError({ text: t.reportError, retry: () => runReport(batchID, farmID, reportType) });
    } finally {
      setLoading(false);
    }
  };

  // After a report: start another one, or jump over to data entry.
  const reportViewNewReport = () => {
    userSay(t.newReport);
    setData({ ...blankData, mode: "report" });
    setError(null);
    botSay(t.whatAnalyze);
    setStep(STEP.REPORT_MENU);
  };

  const reportViewEnterData = () => {
    userSay(t.menuEnterData);
    setData({ ...blankData, mode: "data_entry" });
    setError(null);
    gotoFarmOrFarmer();
  };

  // ---- data entry -------------------------------------------------------
  // A day was tapped: fetch what's already recorded, then ask which category
  // to enter (mobile's "What data would you like to enter?" cards).
  const chooseDay = async (opt) => {
    userSay(`${t.dayPrefix} ${opt.day} · ${opt.value}`);
    setLoading(true);
    setError(null);
    try {
      const res = await chatApi.getExistingValue(data.batchID, opt.value);
      const ex = res?.list || {};
      setData((d) => ({ ...d, date: opt.value, day: opt.day, sexing: !!ex.sexing, existing: ex }));
      botSay(t.whatData);
      setStep(STEP.DAY_ACTIONS);
    } catch (e) {
      setError({ text: t.loadError, retry: () => chooseDay(opt) });
    } finally {
      setLoading(false);
    }
  };

  // Pre-fills the input fields for a category from the existing record.
  const prefillFor = (action, sexing, ex) => {
    const flds = fieldsFor(action, sexing, t);
    const init = {};
    flds.forEach((f) => {
      const v = ex[EXISTING_KEY[f.type]];
      // Weight of 0 is not a real entry -> pre-fill blank; other fields keep 0.
      const isWeight = f.type === "chick" || f.type === "chick_male" || f.type === "chick_female";
      const ok = isWeight ? Number(v) > 0 : v !== undefined && v !== null && v !== "";
      init[f.type] = ok ? String(v) : "";
    });
    setInputs(init);
    setInputErrors({});
    setSaveMsg(null);
  };

  // Tapping a category card -> add or edit that category.
  const pickCategory = (action) => {
    const ex = data.existing || {};
    const sexing = !!ex.sexing;
    const entered = isEntered(ex, action);
    userSay(`${entered ? t.edit : t.add} ${t.actions[action]}`);
    prefillFor(action, sexing, ex);
    setData((d) => ({ ...d, action, sexing }));
    botSay(t.enterValues);
    if (sexing) botSay(t.sexingNote);
    setStep(STEP.ENTER);
  };

  const submitValue = async () => {
    const flds = fieldsFor(data.action, data.sexing, t);
    const errs = {};
    const payload = [];
    let anyValue = false;

    flds.forEach((f) => {
      const raw = (inputs[f.type] || "").trim();
      if (raw === "") return;
      anyValue = true;
      if (!REGEX[f.rule].test(raw)) {
        errs[f.type] = t.invalidNumber;
        return;
      }
      payload.push({
        weight: raw,
        farmID: data.farmID,
        batchID: data.batchID,
        date: data.date,
        type: f.type,
      });
    });

    if (!anyValue) {
      setSaveMsg({ type: "error", text: t.fillAtLeastOne });
      return;
    }
    if (Object.keys(errs).length) {
      setInputErrors(errs);
      return;
    }

    setInputErrors({});
    setSaveMsg(null);
    // Ask the user to confirm before persisting.
    const lines = payload.map((p) => {
      const fld = flds.find((f) => f.type === p.type);
      return { label: fld ? fld.label : p.type, value: p.weight };
    });
    setConfirm({ payload, lines });
  };

  // Performs the actual save once the user confirms. On success the flow
  // returns to a REFRESHED day grid — the mobile loop: the recoloured grid is
  // the review screen, and the user taps a day again for the next category.
  const doSave = async () => {
    if (!confirm) return;
    const { payload, lines } = confirm;
    setSaving(true);
    try {
      const res = await chatApi.saveEntry(payload);
      if (res && res.status === false) {
        setConfirm(null);
        setSaveMsg({ type: "error", text: res.message || t.saveError });
        return;
      }
      setConfirm(null);
      userSay(lines.map((l) => `${l.label}: ${l.value}`).join("\n"));
      botSay((res && typeof res.message === "string" && res.message) || t.saved);
      const batchID = data.batchID;
      setData((d) => ({ ...d, date: "", day: "", action: "", existing: {} }));
      botSay(t.selectDay);
      setStep(STEP.DAY);
      await loadBatchOverview(batchID);
    } catch (e) {
      setConfirm(null);
      setSaveMsg({ type: "error", text: t.saveError });
    } finally {
      setSaving(false);
    }
  };

  // ---- free-text chat ("Ask Roo anything…") -----------------------------
  // Posts to the Django reports/chat/ endpoint the mobile app uses, with the
  // current report (when one was generated) and farm/batch context attached.
  const sendChat = async () => {
    const msg = chatInput.trim();
    if ((!msg && !chatImage) || chatLoading) return;
    setAskedFreeText(true);
    setMessages((m) => [
      ...m,
      { from: "user", text: msg || t.photoAttached, image: chatImage?.url || undefined },
    ]);
    const img = chatImage;
    setChatInput("");
    setChatImage(null);
    setChatLoading(true);
    try {
      const form = new FormData();
      if (msg) form.append("message", msg);
      if (currentReport) form.append("report_analysis", JSON.stringify(currentReport));
      if (data.farmID) form.append("farm_id", String(data.farmID));
      if (data.batchID) form.append("batch_id", String(data.batchID));
      if (data.reportType) form.append("report_type", data.reportType);
      if (img?.file) form.append("image", img.file, img.file.name);
      const res = await chatApi.sendReportChat(form);
      const reply =
        res?.reply ??
        res?.message ??
        res?.data?.reply ??
        res?.data?.message ??
        (typeof res === "string" ? res : null);
      botSay(reply || t.chatError);
    } catch (e) {
      botSay(t.chatError);
    } finally {
      setChatLoading(false);
    }
  };

  const onChatFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) setChatImage({ file, url: URL.createObjectURL(file) });
    e.target.value = ""; // allow re-picking the same file
  };

  // Back navigation for the selection steps.
  const goBack = () => {
    setError(null);
    setSaveMsg(null);
    if (step === STEP.FARMER) {
      if (data.mode === "report") {
        botSay(t.whatAnalyze);
        setStep(STEP.REPORT_MENU);
      } else {
        backToWelcome();
      }
    } else if (step === STEP.FARM) {
      if (isAdmin) {
        botSay(t.selectFarmer);
        setStep(STEP.FARMER);
        loadFarmers();
      } else if (data.mode === "report") {
        botSay(t.whatAnalyze);
        setStep(STEP.REPORT_MENU);
      } else {
        backToWelcome();
      }
    } else if (step === STEP.BATCH) {
      botSay(t.selectFarm);
      setStep(STEP.FARM);
      loadFarms();
    } else if (step === STEP.SUMMARY) {
      botSay(t.selectBatch);
      setStep(STEP.BATCH);
      loadBatches(data.farmID);
    } else if (step === STEP.DAY) {
      // Overview and day options are still in state — just step back.
      setStep(STEP.SUMMARY);
    } else if (step === STEP.DAY_ACTIONS) {
      // Grid data is still in state — just step back.
      setStep(STEP.DAY);
    } else if (step === STEP.ENTER) {
      botSay(t.whatData);
      setStep(STEP.DAY_ACTIONS);
    } else if (step === STEP.REPORT_MENU) {
      backToWelcome();
    } else if (step === STEP.DATE_RANGE) {
      botSay(t.whatAnalyze);
      setStep(STEP.REPORT_MENU);
    }
  };

  const onSelect = (opt) => {
    if (step === STEP.FARMER) return chooseFarmer(opt);
    if (step === STEP.FARM) return chooseFarm(opt);
    if (step === STEP.BATCH) return chooseBatch(opt);
    if (step === STEP.REPORT_MENU) return chooseReportType(opt);
  };

  // Only show for authenticated users.
  if (!isLoggedin()) return null;

  const canGoBack =
    step === STEP.FARMER ||
    step === STEP.FARM ||
    step === STEP.BATCH ||
    step === STEP.SUMMARY ||
    step === STEP.DAY ||
    step === STEP.DAY_ACTIONS ||
    step === STEP.ENTER ||
    step === STEP.REPORT_MENU ||
    step === STEP.DATE_RANGE;

  // ---- reusable bits ----------------------------------------------------
  const BackButton = () => (
    <motion.button
      type="button"
      whileTap={{ scale: 0.95 }}
      onClick={goBack}
      style={{ ...pillStyle(THEME.grey), marginTop: 10, display: "inline-flex", gap: 6 }}
    >
      <FaArrowLeft size={11} /> {t.back}
    </motion.button>
  );

  // ---- launcher (closed) -----------------------------------------------
  const launcher = (
    <motion.button
      key="launcher"
      type="button"
      onClick={launch}
      aria-label={t.launcherLabel}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      transition={{ type: "spring", stiffness: 500, damping: 25 }}
      style={{
        position: "fixed",
        bottom: 22,
        right: 22,
        width: 60,
        height: 60,
        borderRadius: "50%",
        border: "none",
        background: "#fff",
        cursor: "pointer",
        boxShadow: "0 6px 18px rgba(37,99,235,0.45)",
        zIndex: 9999,
      }}
    >
      {/* Subtle ripple — two staggered rings gently radiating outward. */}
      {[0, 1].map((i) => (
        <motion.span
          key={i}
          aria-hidden
          animate={{ scale: [1, 1.8], opacity: [0, 0.3, 0] }}
          transition={{
            repeat: Infinity,
            duration: 3.2,
            ease: "easeOut",
            times: [0, 0.25, 1],
            delay: i * 1.6,
          }}
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: `2px solid ${THEME.blue}`,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      ))}
      <motion.span
        animate={{ rotate: [0, -12, 12, -8, 0] }}
        transition={{ repeat: Infinity, repeatDelay: 3, duration: 0.8 }}
        style={{ display: "inline-flex", position: "relative", zIndex: 1 }}
      >
        <img
          src={MASCOT_IMG}
          alt=""
          style={{ width: 38, height: 38, objectFit: "contain", display: "block" }}
        />
      </motion.span>
    </motion.button>
  );

  // ---- window (open) ----------------------------------------------------
  const windowEl = (
    <motion.div
      key="window"
      initial={{ opacity: 0, scale: 0.85, y: 24 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.85, y: 24 }}
      transition={{ type: "spring", stiffness: 320, damping: 28 }}
      style={{
        position: "fixed",
        bottom: maximized ? 16 : 22,
        right: maximized ? 16 : 22,
        width: maximized ? "min(880px, calc(100vw - 32px))" : 372,
        maxWidth: "calc(100vw - 24px)",
        height: maximized ? "calc(100vh - 32px)" : 564,
        maxHeight: "calc(100vh - 32px)",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 18px 50px rgba(15,23,42,0.30)",
        zIndex: 9999,
        transition: "width 0.3s ease, height 0.3s ease, bottom 0.3s ease, right 0.3s ease",
        fontFamily: "inherit",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: THEME.headerGrad,
          color: "#fff",
          padding: "13px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 15 }}>
          <img
            src={MASCOT_IMG}
            alt=""
            style={{
              width: 28,
              height: 28,
              objectFit: "contain",
              background: "#fff",
              borderRadius: "50%",
              padding: 3,
              boxSizing: "border-box",
            }}
          />
          {t.title}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {/* Only offered when there is something to clear -- on a fresh
              landing screen it would do nothing visible. */}
          {(step !== STEP.LANGUAGE || messages.length > 0) && (
            <motion.button
              type="button"
              whileHover={{ scale: 1.15, rotate: -30 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleRestart}
              aria-label={t.restart}
              title={t.restart}
              style={iconBtn}
            >
              <FaRedo size={13} />
            </motion.button>
          )}
          <motion.button
            type="button"
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setMaximized((m) => !m)}
            aria-label={maximized ? "Restore" : "Maximize"}
            style={iconBtn}
          >
            {maximized ? <FaCompress size={14} /> : <FaExpand size={14} />}
          </motion.button>
          <motion.button
            type="button"
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleClose}
            aria-label="Close"
            style={iconBtn}
          >
            <FaTimes size={16} />
          </motion.button>
        </div>
      </div>

      {/* Context bar — keeps the active Day & Date prominent during a chain of entries */}
      <AnimatePresence>
        {data.date && (step === STEP.ENTER || step === STEP.DAY_ACTIONS) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              padding: "8px 14px",
              background: "#e0ecff",
              borderBottom: "1px solid #c7dbff",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: "#475569",
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {data.batchName}
            </span>
            <span
              style={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: THEME.headerGrad,
                color: "#fff",
                fontWeight: 700,
                fontSize: 12.5,
                borderRadius: 14,
                padding: "4px 11px",
                boxShadow: "0 2px 6px rgba(37,99,235,0.30)",
              }}
            >
              <FaCalendarAlt size={11} />
              {t.dayPrefix} {data.day} · {data.date}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 14, background: "#f8fafc" }}>
        {step === STEP.LANGUAGE && <WelcomeHero t={t} name={firstName()} />}

        {messages.map((m, i) =>
          m.report ? (
            <ReportCard key={i} report={m.report} t={t} />
          ) : (
            <MessageBubble key={i} from={m.from} text={m.text} image={m.image} />
          )
        )}

        {(loading || chatLoading) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ display: "flex", gap: 4, padding: "4px 2px" }}
          >
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                animate={{ y: [0, -5, 0] }}
                transition={{ repeat: Infinity, duration: 0.6, delay: i * 0.15 }}
                style={{ width: 7, height: 7, borderRadius: "50%", background: THEME.blue }}
              />
            ))}
          </motion.div>
        )}

        {error && (
          <div style={{ marginTop: 6 }}>
            <MessageBubble from="bot" text={error.text} />
          </div>
        )}
      </div>

      {/* Footer / interaction area */}
      <div
        style={{
          borderTop: "1px solid #eef2f7",
          padding: 12,
          background: "#fff",
          maxHeight: "55%",
          overflowY: "auto",
          flexShrink: 0,
        }}
      >
        {/* Language step */}
        {step === STEP.LANGUAGE && (
          <motion.div
            variants={listContainer}
            initial="hidden"
            animate="visible"
            style={{ display: "flex", gap: 10 }}
          >
            {[
              { code: "EN", flag: "🇬🇧", label: "English" },
              { code: "BM", flag: "🇲🇾", label: "Bahasa Melayu" },
            ].map((l) => (
              <motion.button
                key={l.code}
                type="button"
                variants={listItem}
                whileHover={{ scale: 1.04, y: -2, borderColor: THEME.blue }}
                whileTap={{ scale: 0.96 }}
                onClick={() => chooseLanguage(l.code)}
                style={{
                  flex: 1,
                  border: "1px solid #dbe6ff",
                  background: "#fff",
                  borderRadius: 14,
                  padding: "13px 8px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 7,
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(37,99,235,0.10)",
                }}
              >
                <span style={{ fontSize: 26, lineHeight: 1 }}>{l.flag}</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: THEME.ink }}>{l.label}</span>
              </motion.button>
            ))}
          </motion.div>
        )}

        {/* Error retry / back */}
        {error && (
          <div style={{ display: "flex", gap: 8 }}>
            <motion.button
              type="button"
              whileTap={{ scale: 0.95 }}
              onClick={error.retry}
              style={{ ...pillStyle(THEME.blue), display: "inline-flex", gap: 6 }}
            >
              <FaRedo size={11} /> {t.retry}
            </motion.button>
            {canGoBack && (
              <motion.button
                type="button"
                whileTap={{ scale: 0.95 }}
                onClick={goBack}
                style={{ ...pillStyle(THEME.grey), display: "inline-flex", gap: 6 }}
              >
                <FaArrowLeft size={11} /> {t.back}
              </motion.button>
            )}
          </div>
        )}

        {/* Welcome menu — the mobile flow's two branches. Hidden once the
            user has asked a free-text question: the menu is a starting
            prompt, and leaving it pinned under a real answer reads as though
            it belongs to that answer. The header's restart brings it back. */}
        {!error && step === STEP.WELCOME && !loading && !askedFreeText && (
          <motion.div
            variants={listContainer}
            initial="hidden"
            animate="visible"
            style={{ display: "flex", gap: 10 }}
          >
            {[
              { label: t.menuEnterData, Icon: FaClipboardList, onClick: chooseEnterData },
              { label: t.menuReports, Icon: FaChartLine, onClick: chooseReports },
            ].map(({ label, Icon, onClick }) => (
              <motion.button
                key={label}
                type="button"
                variants={listItem}
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.96 }}
                onClick={onClick}
                style={{
                  flex: 1,
                  border: "none",
                  borderRadius: 14,
                  padding: "16px 8px",
                  background: THEME.blueGrad,
                  color: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 3px 10px rgba(37,99,235,0.30)",
                }}
              >
                <Icon size={22} />
                <span style={{ fontSize: 12.5, fontWeight: 600 }}>{label}</span>
              </motion.button>
            ))}
          </motion.div>
        )}

        {/* Report type menu */}
        {!error && step === STEP.REPORT_MENU && !loading && (
          <div>
            <SelectList
              items={REPORT_TYPES.map((v) => ({ label: t.reportTypes?.[v] || v, value: v }))}
              Icon={FaChartLine}
              t={t}
              onSelect={onSelect}
            />
            <BackButton />
          </div>
        )}

        {/* Custom date range */}
        {!error && step === STEP.DATE_RANGE && !loading && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            {[
              { key: "startDate", label: t.rangeStart },
              { key: "endDate", label: t.rangeEnd },
            ].map(({ key, label }) => (
              <div key={key} style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: 12, color: "#64748b", marginBottom: 4 }}>
                  {label}
                </label>
                <input
                  type="date"
                  value={data[key]}
                  max={moment().tz("Asia/Singapore").format("YYYY-MM-DD")}
                  onChange={(e) => setData((d) => ({ ...d, [key]: e.target.value }))}
                  style={{
                    width: "100%",
                    padding: "9px 11px",
                    border: "1px solid #dbe3ee",
                    borderRadius: 10,
                    fontSize: 14,
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
              </div>
            ))}
            {saveMsg && (
              <div style={{ color: THEME.red, fontSize: 12, marginBottom: 8 }}>{saveMsg.text}</div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <motion.button
                type="button"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={applyRange}
                style={{ ...pillStyle(THEME.blue), display: "inline-flex", gap: 6 }}
              >
                {t.applyRange} <FaChevronRight size={11} />
              </motion.button>
              <motion.button
                type="button"
                whileTap={{ scale: 0.95 }}
                onClick={goBack}
                style={{ ...pillStyle(THEME.grey), display: "inline-flex", gap: 6 }}
              >
                <FaArrowLeft size={11} /> {t.back}
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Farmer selection — admin-only, cards */}
        {!error && step === STEP.FARMER && !loading && (
          <div>
            {options.length > 0 && (
              <SelectList items={options} Icon={FaUserTie} t={t} onSelect={onSelect} />
            )}
            <BackButton />
          </div>
        )}

        {/* Farm selection — cards */}
        {!error && step === STEP.FARM && !loading && (
          <div>
            {options.length > 0 && (
              <SelectList items={options} Icon={FaTractor} t={t} onSelect={onSelect} />
            )}
            <BackButton />
          </div>
        )}

        {/* Batch selection — cards */}
        {!error && step === STEP.BATCH && !loading && (
          <div>
            {options.length > 0 && (
              <SelectList items={options} Icon={FaLayerGroup} t={t} onSelect={onSelect} />
            )}
            <BackButton />
          </div>
        )}

        {/* Batch overview — read the batch, then Proceed to the day grid. */}
        {!error && step === STEP.SUMMARY && !loading && growth && (
          <BatchSummary
            batchName={data.batchName}
            growth={growth}
            t={t}
            onProceed={proceedToDays}
            onBack={goBack}
          />
        )}

        {/* Day selection — colour-coded grid (mobile's day cards).
            Future days are hidden; a note tells the user how many remain. */}
        {!error && step === STEP.DAY && !loading && (() => {
          const visibleDays = options.filter((it) => !it.isFuture);
          const daysLeft = options.length - visibleDays.length; // hidden future days
          const hasToday = visibleDays.some((it) => it.isToday);
          // A note tailored to where the batch sits in its scheduled timeline.
          let note = null;
          if (options.length === 0) {
            note = null;
          } else if (visibleDays.length === 0) {
            // Whole schedule is still ahead — batch hasn't started yet.
            note = { text: t.batchNotStarted.replace("{date}", options[0].value), done: false };
          } else if (daysLeft > 0) {
            note = {
              text: (daysLeft === 1 ? t.dayLeft : t.daysLeft).replace("{n}", daysLeft),
              done: false,
            };
          } else if (hasToday) {
            // Today is the last scheduled day.
            note = { text: t.batchFinalDay, done: true };
          } else {
            // Today is past the last scheduled day — batch has over-run its duration.
            note = { text: t.batchCompleted, done: true };
          }
          return (
            <div>
              <DayGrid
                days={visibleDays}
                statusMap={growth?.dayStatusMap}
                statusOk={!!growth?.statusOk}
                selectedDate={data.date}
                note={note}
                t={t}
                onPick={chooseDay}
              />
              <BackButton />
            </div>
          );
        })()}

        {/* Per-day category cards — "What data would you like to enter?" */}
        {!error && step === STEP.DAY_ACTIONS && !loading && (
          <div>
            <CategoryCards
              actions={ACTIONS}
              isEntered={(a) => isEntered(data.existing, a)}
              summaryFor={(a) => existingSummary(data.existing, a, data.sexing)}
              t={t}
              onPick={pickCategory}
            />
            <BackButton />
          </div>
        )}

        {/* Report view — a report card is in the transcript above */}
        {!error && step === STEP.REPORT_VIEW && !loading && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <motion.button
              type="button"
              whileTap={{ scale: 0.95 }}
              onClick={reportViewNewReport}
              style={pillStyle(THEME.blue)}
            >
              {t.newReport}
            </motion.button>
            <motion.button
              type="button"
              whileTap={{ scale: 0.95 }}
              onClick={reportViewEnterData}
              style={pillStyle(THEME.grey)}
            >
              {t.menuEnterData}
            </motion.button>
            <motion.button
              type="button"
              whileTap={{ scale: 0.95 }}
              onClick={handleClose}
              style={pillStyle(THEME.grey)}
            >
              {t.close}
            </motion.button>
          </div>
        )}

        {/* Value entry */}
        {!error && step === STEP.ENTER && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            {fieldsFor(data.action, data.sexing, t).map((f) => (
              <div key={f.type} style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: 12, color: "#64748b", marginBottom: 4 }}>
                  {f.label}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={inputs[f.type] || ""}
                  disabled={saving}
                  onChange={(e) => setInputs((prev) => ({ ...prev, [f.type]: e.target.value }))}
                  onFocus={(e) => (e.target.style.borderColor = THEME.blue)}
                  onBlur={(e) =>
                    (e.target.style.borderColor = inputErrors[f.type] ? THEME.red : "#dbe3ee")
                  }
                  style={{
                    width: "100%",
                    padding: "9px 11px",
                    border: `1px solid ${inputErrors[f.type] ? THEME.red : "#dbe3ee"}`,
                    borderRadius: 10,
                    fontSize: 14,
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
                {inputErrors[f.type] && (
                  <span style={{ color: THEME.red, fontSize: 11 }}>{inputErrors[f.type]}</span>
                )}
              </div>
            ))}

            {saveMsg && (
              <div style={{ color: THEME.red, fontSize: 12, marginBottom: 8 }}>{saveMsg.text}</div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <motion.button
                type="button"
                whileHover={saving ? undefined : { scale: 1.04 }}
                whileTap={saving ? undefined : { scale: 0.96 }}
                onClick={submitValue}
                disabled={saving}
                style={{
                  ...pillStyle(THEME.blue),
                  display: "inline-flex",
                  gap: 6,
                  opacity: saving ? 0.7 : 1,
                }}
              >
                <FaCheck size={11} /> {saving ? t.saving : t.save}
              </motion.button>
              <motion.button
                type="button"
                whileTap={{ scale: 0.96 }}
                onClick={goBack}
                disabled={saving}
                style={{ ...pillStyle(THEME.grey), display: "inline-flex", gap: 6 }}
              >
                <FaArrowLeft size={11} /> {t.back}
              </motion.button>
            </div>
          </motion.div>
        )}
      </div>

      {/* "Ask Roo anything…" — always available once a language is chosen,
          like the mobile app's persistent chat bar. */}
      {step !== STEP.LANGUAGE && (
        <div style={{ borderTop: "1px solid #eef2f7", background: "#fff", flexShrink: 0 }}>
          {chatImage && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 12px 0",
              }}
            >
              <img
                src={chatImage.url}
                alt=""
                style={{ width: 34, height: 34, objectFit: "cover", borderRadius: 8 }}
              />
              <button
                type="button"
                onClick={() => setChatImage(null)}
                style={{
                  background: "none",
                  border: "none",
                  color: THEME.red,
                  fontSize: 11.5,
                  fontWeight: 700,
                  cursor: "pointer",
                  padding: "2px 4px",
                }}
              >
                {t.removePhoto}
              </button>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px" }}>
            <input
              ref={chatFileRef}
              type="file"
              accept="image/*"
              onChange={onChatFile}
              style={{ display: "none" }}
            />
            <motion.button
              type="button"
              whileTap={{ scale: 0.9 }}
              onClick={() => chatFileRef.current && chatFileRef.current.click()}
              aria-label={t.attachPhoto}
              style={{
                flexShrink: 0,
                width: 34,
                height: 34,
                borderRadius: "50%",
                border: "1px solid #dbe3ee",
                background: "#fff",
                color: THEME.grey,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaImage size={14} />
            </motion.button>
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendChat();
                }
              }}
              placeholder={t.askPlaceholder}
              disabled={chatLoading}
              style={{
                flex: 1,
                padding: "9px 12px",
                border: "1px solid #dbe3ee",
                borderRadius: 18,
                fontSize: 13.5,
                outline: "none",
                boxSizing: "border-box",
                background: chatLoading ? "#f8fafc" : "#fff",
              }}
            />
            <motion.button
              type="button"
              whileTap={{ scale: 0.9 }}
              onClick={sendChat}
              disabled={chatLoading || (!chatInput.trim() && !chatImage)}
              aria-label={t.chatSend}
              style={{
                flexShrink: 0,
                width: 34,
                height: 34,
                borderRadius: "50%",
                border: "none",
                background:
                  chatLoading || (!chatInput.trim() && !chatImage) ? "#cbd5e1" : THEME.blueGrad,
                color: "#fff",
                cursor:
                  chatLoading || (!chatInput.trim() && !chatImage) ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaPaperPlane size={13} />
            </motion.button>
          </div>
        </div>
      )}

      {/* In-window confirmation modal */}
      <AnimatePresence>
        {confirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15,23,42,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 18,
              zIndex: 20,
            }}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 10 }}
              transition={{ type: "spring", stiffness: 320, damping: 26 }}
              style={{
                width: "100%",
                maxWidth: 320,
                background: "#fff",
                borderRadius: 16,
                overflow: "hidden",
                boxShadow: "0 12px 40px rgba(15,23,42,0.35)",
              }}
            >
              <div
                style={{
                  background: THEME.headerGrad,
                  color: "#fff",
                  padding: "12px 16px",
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {t.confirmTitle}
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ fontSize: 13, color: "#475569", marginBottom: 10 }}>
                  {t.confirmSave.replace("{day}", `${t.dayPrefix} ${data.day} · ${data.date}`)}
                </div>
                <div
                  style={{
                    background: "#f1f5ff",
                    border: "1px solid #dbe6ff",
                    borderRadius: 10,
                    padding: "10px 12px",
                    marginBottom: 16,
                  }}
                >
                  {confirm.lines.map((l, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        fontSize: 13.5,
                        padding: "3px 0",
                      }}
                    >
                      <span style={{ color: "#475569" }}>{l.label}</span>
                      <span style={{ fontWeight: 700, color: THEME.ink }}>{l.value}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <motion.button
                    type="button"
                    whileHover={saving ? undefined : { scale: 1.04 }}
                    whileTap={saving ? undefined : { scale: 0.96 }}
                    onClick={doSave}
                    disabled={saving}
                    style={{
                      ...pillStyle(THEME.green),
                      flex: 1,
                      justifyContent: "center",
                      display: "inline-flex",
                      gap: 6,
                      opacity: saving ? 0.7 : 1,
                    }}
                  >
                    <FaCheck size={11} /> {saving ? t.saving : t.confirm}
                  </motion.button>
                  <motion.button
                    type="button"
                    whileTap={{ scale: 0.96 }}
                    onClick={() => setConfirm(null)}
                    disabled={saving}
                    style={{
                      ...pillStyle(THEME.grey),
                      flex: 1,
                      justifyContent: "center",
                      display: "inline-flex",
                    }}
                  >
                    {t.cancel}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  return (
    <>
      <AnimatePresence>{open && windowEl}</AnimatePresence>
      <AnimatePresence>{!open && launcher}</AnimatePresence>
    </>
  );
};

const pillStyle = (bg) => ({
  background: bg,
  color: "#fff",
  border: "none",
  borderRadius: 20,
  padding: "9px 16px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  alignItems: "center",
});

const iconBtn = {
  background: "rgba(255,255,255,0.18)",
  border: "none",
  color: "#fff",
  width: 28,
  height: 28,
  borderRadius: 8,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export default ChickenChatWidget;
